<template>
  <form @submit.prevent="validateForm()">
    <div class="d-flex vin-row" :class="{ 'has-error': errors.has('vin') }">
      <base-input
        v-model.trim="vehicleData.vin"
        ref="vin"
        v-validate="'required|alpha_num|min:17'"
        label="VIN:"
        placeholder="VIN"
        name="vin"
        maxlength="17"
        data-vv-as="VIN"
        :error-messages="errors.has('vin') ? errors.collect('vin') : []"
      />
      <base-button :disabled="errors.has('vin')" @click.prevent="decodeVin()" padding="3px 14px">
        Decode
      </base-button>
    </div>

    <base-select
      label="Year:"
      v-model="vehicleData.year"
      name="year"
      :disabled="!!decodedVehicle.model_year"
      v-validate="'required'"
      :options="formOptions.year"
      data-vv-as="year"
      :error-messages="errors.has('year') ? errors.collect('year') : []"
    />

    <base-select
      label="Make:"
      v-model="vehicleData.make"
      name="make"
      :disabled="!!decodedVehicle.make"
      v-validate="'required'"
      :options="formOptions.make"
      data-vv-as="make"
      :error-messages="errors.has('make') ? errors.collect('make') : []"
    />

    <base-select
      label="Model:"
      v-model="vehicleData.model"
      name="model"
      :disabled="!!decodedVehicle.model"
      v-validate="'required'"
      :options="formOptions.model"
      data-vv-as="model"
      :error-messages="errors.has('model') ? errors.collect('model') : []"
    />

    <base-input v-model.trim="vehicleData.trim" :disabled="!!decodedVehicle.trim" label="Trim" />

    <base-input
      v-model="mileage"
      v-validate="'required|used_car_mileage'"
      label="Mileage:"
      name="mileage"
      data-vv-as="mileage"
      :error-messages="errors.has('mileage') ? errors.collect('mileage') : []"
    />

    <base-select
      label="Trans:"
      v-model="vehicleData.trans"
      name="trans"
      :disabled="!!decodedVehicle.trans"
      v-validate="'required'"
      :options="formOptions.trans"
      data-vv-as="transmission"
      :error-messages="errors.has('trans') ? errors.collect('trans') : []"
    />

    <base-select
      label="Ext Color:"
      v-model="vehicleData.ext_color"
      name="ext_color"
      :disabled="!!decodedVehicle.color"
      v-validate="'required'"
      :options="formOptions.color"
      data-vv-as="exterior color"
      :error-messages="errors.has('ext_color') ? errors.collect('ext_color') : []"
    />

    <base-select
      label="Int Color:"
      v-model="vehicleData.int_color"
      name="int_color"
      placeholder="Select a color"
      :disabled="!!decodedVehicle.color"
      v-validate="'required'"
      :options="formOptions.color"
      data-vv-as="interior color"
      :error-messages="errors.has('int_color') ? errors.collect('int_color') : []"
    />

    <base-select
      label="Body Sty:"
      v-model="vehicleData.body_style"
      name="body_style"
      placeholder="Select a color"
      :disabled="!!decodedVehicle.body_type"
      v-validate="'required'"
      :options="formOptions.body_style"
      data-vv-as="body style"
      :error-messages="errors.has('body_style') ? errors.collect('body_style') : []"
    />

    <div class="d-flex justify-content-center mt-3 mb-4">
      <base-button class="btn-blue" type="submit">Submit Information</base-button>
    </div>
  </form>
</template>

<script>
import { formatMiles } from '@/util/formatters';
import BaseInput from '@/components/base-input/';
import { ref } from '@vue/composition-api';
import BaseButton from '@/components/base-button/';
import BaseSelect from '@/components/base-select/';
import InvalidVinModal from '@/components/invalid-vin-modal';
import VehicleService from '@/services/VehicleService';
import { tradeFormStateHelper } from '@/composable/tradeFormStateHelper';
import useLocalStorage from '@/composable/useLocalStorage';

export default {
  name: 'VehicleInformation',
  components: {
    BaseInput,
    BaseButton,
    BaseSelect,
  },
  props: {
    formOptions: {
      type: Object,
      required: true,
    },
    initialData: {
      type: Object,
      default: function () {
        return {};
      },
    },
    localStorageKey: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const decodedVehicle = useLocalStorage(`decodedVehicle-${props.localStorageKey}`, {});
    let vehicleData = ref({
      vin: '',
      year: '',
      make: '',
      model: '',
      trim: '',
      mileage: '',
      trans: '',
      ext_color: '',
      int_color: '',
      body_style: '',
    });

    tradeFormStateHelper(props, vehicleData, emit);

    return { vehicleData, decodedVehicle };
  },
  computed: {
    mileage: {
      get() {
        return formatMiles(this.vehicleData.mileage);
      },
      set(value) {
        this.vehicleData.mileage = value.replace(/,/g, '');
      },
    },
  },
  created() {
    this.$validator.extend('used_car_mileage', {
      getMessage: (field) =>
        `The ${field} field is out of the allowed range. Max is: 99,999,999,999`,
      validate: (value) => {
        const numericValue = Number.parseInt(value.replace(/,/g, ''));
        const match = value.match(/^((?:\d{1,2},)?(?:\d{1,3},?){1,3})$/) || [];
        return numericValue > 0 && match.length > 0;
      },
    });
  },
  methods: {
    formatVin(vinData) {
      return Object.entries(vinData).reduce((data, [key, value]) => {
        const formattedKey = typeof key == 'string' ? key.toLowerCase() : key;
        data[formattedKey] = value;
        return data;
      }, {});
    },
    updateFormProp(optionName, optionData) {
      const localOption = this.formOptions[optionName];

      if (optionData && localOption) {
        const hasOption = localOption.find((option) => option === optionData);

        if (!hasOption) {
          return [...this.formOptions[optionName], optionData];
        }
      }
      return this.formOptions[optionName];
    },
    decodeVin() {
      this.$store.commit('setShowAppLoader', true);

      VehicleService.decodeVehicleByVin(this.vehicleData.vin)
        .then(({ data }) => {
          const [firstVehicle] = data;

          const formattedVin = this.formatVin(firstVehicle);
          this.vehicleData = this.mapToVehicleData(formattedVin);
          this.decodedVehicle = { ...formattedVin };

          const localFormData = { ...this.formOptions };
          localFormData.make = this.updateFormProp('make', this.decodedVehicle.make);
          localFormData.model = this.updateFormProp('model', this.decodedVehicle.model);
          localFormData.year = this.updateFormProp('year', Number(this.decodedVehicle.model_year));
          localFormData.body_style = this.updateFormProp(
            'body_style',
            this.decodedVehicle.body_type
          );
          this.$emit('update:form-options', localFormData);
        })
        .catch((e) => {
          const isVINInvalidStatus = e.response.status === 406;
          if (isVINInvalidStatus) {
            this.handleInvalidVin();
            this.$emit('invalid-vin', e);
          }
        })
        .finally(() => {
          this.$store.commit('setShowAppLoader', false);
        });
    },
    async validateForm() {
      const isValid = await this.$validator.validateAll();
      if (isValid) {
        this.$emit('submit', this.vehicleData);
      }
    },
    handleInvalidVin() {
      const modalName = 'invalid-vin-modal';
      const vModalProps = {
        modalName,
      };
      const vModalProperties = {
        name: modalName,
        adaptative: true,
        clickToClose: false,
        classes: ['v--modal', 'invalid-vin-modal'],
        transition: 'slide-right',
        width: 300,
        height: '100%',
        shiftX: 1,
        shiftY: 0,
      };
      const vModalEventListeners = {
        'before-close': this.invalidVinCallback,
      };
      this.$modal.show(InvalidVinModal, vModalProps, vModalProperties, vModalEventListeners);
    },
    invalidVinCallback(payload) {
      const {
        params: { action },
      } = payload;

      switch (action) {
        case 'new':
          this.vehicleData.vin = '';
          this.$refs.vin.$refs.input.focus();
          break;
        case 'proceed':
        default:
          break;
      }
    },
    mapToVehicleData(data) {
      const { model, make, trim, model_year: year, body_type: body_style } = data;

      return {
        ...this.vehicleData,
        year,
        make,
        model,
        trim,
        body_style,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.vin-row {
  flex-wrap: nowrap;
  align-items: baseline;

  &.has-error {
    align-items: flex-start;

    button {
      margin-top: 0.8rem;
    }
  }
}
</style>
